import React, { useEffect, useMemo } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Bell, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { EnhancedSidebar } from '@/components/common/EnhancedSidebar';
import { useIsAuthenticated } from '@/hooks/redux';
import { cn, formatNumberWithCommas, showToastMessage } from '@/utils';
import { RootState } from '@/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  useAvailbleRolesQuery,
  useGetUserQuery,
  useGetWalletQuery,
  useGetMfaStatusQuery,
  useLogoutMutation,
} from '@/store/api/authApi';
import { useGetDepositWithdrawCountsQuery } from '@/store/api/bankerApi';
import { clearUser } from '@/store/slices/userSlice';
import { logout as clearAuth } from '@/store';
import apiSlice from '@/store/api/apiSlice';
import { STATUS_LOGOUT_MESSAGES } from '@/utils/constent';
import { Modal } from '@/components/common/Modal';
import { ChangePassword } from '@/components/common/ChangePassword';
import { MfaSettingsModal } from '@/features/mfa/MfaSettingsModal';
import { setDepositWithdrawCounts } from '@/store/slices/depositWithdrawCountsSlice';
import { useDepositWithdrawRequestSound } from '@/hooks/useDepositWithdrawRequestSound';
export function DashboardLayout() {
  const [logout] = useLogoutMutation();
  const BLOCKED_STATUSES = ['inactive', 'suspended'];
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const ismobile = window.innerWidth < 768;
  const [sidebarOpen, setSidebarOpen] = React.useState(!ismobile);
  const [mfaModalOpen, setMfaModalOpen] = React.useState(false);
  const [mfaAction, setMfaAction] = React.useState<'enable' | 'disable'>('enable');
  const isAuthenticated = useIsAuthenticated();
  useDepositWithdrawRequestSound(isAuthenticated);
  const { data: userData } = useGetUserQuery(undefined, {
    skip: !isAuthenticated,
    pollingInterval: 20000,
  });

  useAvailbleRolesQuery(undefined, { skip: !isAuthenticated });
  const { data: mfaStatus, isFetching: isFetchingMfaStatus, refetch: refetchMfaStatus } =
    useGetMfaStatusQuery(undefined, { skip: !isAuthenticated });
  const { data } = useGetWalletQuery(undefined, { skip: !isAuthenticated, pollingInterval: 20000 });
  const { data: depositWithdrawCounts } = useGetDepositWithdrawCountsQuery(undefined, {
    skip: !isAuthenticated,
    pollingInterval: 2000,
  });
  const [userClosed, setUserClosed] = React.useState(false);
  const user = useSelector((state: RootState) => state.user);
  const closeModal = () => setUserClosed(true);
  const showModal = Boolean(user.data && user.data.passwordChanged === false && !userClosed);
  const totalPendingCount = depositWithdrawCounts?.totalPendingCount ?? 0;

  useEffect(() => {
    if (depositWithdrawCounts) {
      dispatch(setDepositWithdrawCounts(depositWithdrawCounts));
    }
  }, [depositWithdrawCounts, dispatch]);
  const mainAmount = useMemo(() => {
    return data?.find((w) => w.type === 'Main')?.amount ?? '0';
  }, [data]);
  useEffect(() => {
    if (!userData?.status) return;

    if (BLOCKED_STATUSES.includes(userData.status.toLowerCase())) {
      // 🔹 Clear auth (example)
      const message =
        STATUS_LOGOUT_MESSAGES[userData?.status.toLowerCase()] ??
        'Your session has ended. Please log in again.';

      handleLogout(message);
      navigate('/login', { replace: true });
    }
  }, [userData?.status, navigate]);
  const handleLogout = async (message: string) => {
    try {
      await logout().unwrap();
      dispatch(clearAuth());
      dispatch(clearUser());
      dispatch(apiSlice.util.resetApiState());

      showToastMessage('error', message);
      navigate('/login');
    } catch (err: any) {
      showToastMessage('error', err?.data?.message || 'Failed to logout. Please try again.');
    }
  };
  const finishMfaSetup = async () => {
    try {
      await logout().unwrap();
    } catch {
      // The local session still needs to end after MFA setup, even if logout is already invalid.
    } finally {
      dispatch(clearAuth());
      dispatch(clearUser());
      dispatch(apiSlice.util.resetApiState());
      setMfaModalOpen(false);
      navigate('/login', { replace: true });
    }
  };

  const finishMfaDisable = () => {
    refetchMfaStatus();
    setMfaModalOpen(false);
  };

  const openMfaModal = (action: 'enable' | 'disable') => {
    setMfaAction(action);
    setMfaModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-background ">
      {/* Enhanced Sidebar */}
      <EnhancedSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />

      {/* Main Content */}
      <div className={cn('transition-all duration-300', sidebarOpen ? 'lg:ml-72' : 'lg:ml-16')}>
        {/* Top Navigation */}
        <header className="sticky top-0 z-40 border-b border-green3 bg-header backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex h-16 items-center px-4 ">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-white hover:text-white"
            // className="md:hidden"
            >
              <Menu className="h-5 w-5" />
            </Button>

            <div className="ml-auto flex items-center gap-3 text-white">
              <button
                type="button"
                aria-label={`Pending deposit and withdraw requests: ${totalPendingCount}`}
                className="relative rounded-md p-2 hover:bg-white/10"
                onClick={() => navigate('/requests/Credit/all')}
              >
                <Bell className="h-5 w-5" />
                {totalPendingCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white">
                    {totalPendingCount > 99 ? '99+' : totalPendingCount}
                  </span>
                )}
              </button>
              <span className="max-md:hidden text-sm font-medium">Multi-Factor Authentication</span>
              <span className="md:hidden text-sm font-medium">MFA</span>
              <Switch
                aria-label="Toggle MFA"
                className="data-[state=checked]:bg-emerald-700 data-[state=unchecked]:bg-slate-500 [&>span]:!bg-white"
                checked={Boolean(mfaStatus?.isMfaEnabled)}
                disabled={isFetchingMfaStatus}
                onCheckedChange={(checked) => openMfaModal(checked ? 'enable' : 'disable')}
              />
            </div>

            <div className="border border-green3 rounded-lg flex items-center gap-2 px-4 text-white">
              <img src="/images/profile.png" alt="" className="h-10 w-10" />
              <div className="flex flex-col justify-start items-start py-1 font-semibold">
                <span className="leading-4">{user?.data?.username || user?.data?.firstname || 'Owner'}</span>
                <span className="flex items-center">{formatNumberWithCommas(mainAmount)}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1  py-6 bg-main-bg min-h-[calc(100dvh-65px)]">
          <Outlet />
        </main>
      </div>
      {showModal && (
        <Modal
          open={showModal}
          style="top-[25%] left-1/2 -translate-x-1/2  bg-black border-green rounded-2xl shadow-sm "
          bgBlur={true}
        >
          <ChangePassword close={closeModal} title={true} />
        </Modal>
      )}
      <MfaSettingsModal
        open={mfaModalOpen}
        action={mfaAction}
        activeMethod={mfaStatus?.activeMethod ?? null}
        defaultUsername={user?.data?.username}
        onOpenChange={setMfaModalOpen}
        onConfigured={finishMfaSetup}
        onDisabled={finishMfaDisable}
      />
    </div>
  );
}
